import React, { useEffect } from "react"
import { Box, Skeleton, styled } from "@mui/material"

import { colors } from "theme"
import { useAppDispatch, useAppSelector } from "redux/hooks"
import { fetchDebtPayments } from "redux/features/debt_payment/debtPaymentThunk"
import {
  selectFetchCollectionStatus,
  selectDebtPayments,
  selectTotal,
} from "redux/features/debt_payment/debtPaymentSlice"
import LoadableCollection from "components/ui/LoadableCollection"
import { DebtPaymentListType } from "types"

export function withDebtPayments<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const WithDebtPayments = (props: GenericType) => {
    const dispatch = useAppDispatch()

    const total = useAppSelector((state) => selectTotal(state))
    const payments = useAppSelector((state) =>
      selectDebtPayments(state),
    ) as DebtPaymentListType

    const fetchStatus = useAppSelector((state) =>
      selectFetchCollectionStatus(state),
    )

    const handleFetchDebtPayments = () => {
      if (!fetchStatus.isFetching) {
        dispatch(fetchDebtPayments())
      }
    }

    useEffect(() => handleFetchDebtPayments(), [])

    const renderSkeleton = () => {
      return (
        <Container>
          <Card sx={{ height: 500 }} animation="pulse" variant="rectangular" />
        </Container>
      )
    }

    return (
      <LoadableCollection
        fetchStatus={fetchStatus}
        renderLoading={renderSkeleton}
      >
        {() => (
          <WrappedComponent
            {...props}
            total={total}
            payments={payments}
            fetchStatus={fetchStatus}
            fetchDebts={handleFetchDebtPayments}
          />
        )}
      </LoadableCollection>
    )
  }

  return WithDebtPayments
}

const Container = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin: 42px 0 0 0;
`

const Card = styled(Skeleton)`
  background-color: ${colors.cultured};
  box-shadow: 12px 14px 23px -2px rgb(0 0 0 / 3%);
  border-radius: 26px;
  margin-bottom: 16px;
  padding: 58px;
  width: 100%;
`
